import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  CheckoutDiscountProfileAttributesCC,
  CheckoutInitialisation,
  CheckoutSuccessfulContract,
  CustomerDetails,
  PaymentFrequency,
  ProtectionDetailsPaymentOption,
  QuoteResponse,
  RegisteredItem,
  RegisteredItemProductDetails,
  RegistrationApplianceDetails,
  RegistrationRequest,
  RegistrationSystemCode,
  RegistriaUsersResponse,
  SerializationCodeResponse,
} from '@us-registration/util-models';
import { registeredItemToRegisteredItemProductDetails } from '../utils/registration/registration.utils';
import {
  REGISTRATION_FEATURE_KEY,
  RegistrationState,
} from './registration.reducer';

export const selectRegistrationState = createFeatureSelector<RegistrationState>(
  REGISTRATION_FEATURE_KEY
);

export const selectRegisteredItem = createSelector(
  selectRegistrationState,
  (state: RegistrationState): RegisteredItem | undefined => state.registeredItem
);

export const selectSerializationCodeResponse = createSelector(
  selectRegistrationState,
  (state: RegistrationState): SerializationCodeResponse | undefined =>
    state.serializationCodeResponse
);

export const selectRegistrationSystemCode = createSelector(
  selectRegistrationState,
  (state: RegistrationState): RegistrationSystemCode =>
    state.registrationSystemCode
);

export const selectRegistrationRequest = createSelector(
  selectRegistrationState,
  (state: RegistrationState): RegistrationRequest | undefined =>
    state.registrationRequest
);

export const selectHasRegisteredItem = createSelector(
  selectRegisteredItem,
  (registeredItem: RegisteredItem | undefined): boolean =>
    Boolean(registeredItem)
);

export const selectRegistrationLoading = createSelector(
  selectRegistrationState,
  (state: RegistrationState): boolean => state.registrationLoading
);

export const selectRegisteredItemProducts = createSelector(
  selectRegisteredItem,
  (
    registeredItem: RegisteredItem | undefined
  ): RegisteredItemProductDetails[] =>
    registeredItemToRegisteredItemProductDetails(registeredItem)
);

export const selectRegisteredItemOutOfGuarantee = createSelector(
  selectRegisteredItem,
  (registeredItem: RegisteredItem | undefined): boolean =>
    Boolean(registeredItem?.outOfGuarantee)
);

export const selectRegisteredItemProductDetails = createSelector(
  selectRegisteredItemProducts,
  (
    registeredItemProductDetails: RegisteredItemProductDetails[]
  ): RegisteredItemProductDetails => registeredItemProductDetails[0]
);

export const selectRegisteredItemAnnualProductDetails = createSelector(
  selectRegisteredItemProducts,
  (
    registeredItemProductDetails: RegisteredItemProductDetails[]
  ): RegisteredItemProductDetails => registeredItemProductDetails[1]
);

export const selectHasRegisteredItemProducts = createSelector(
  selectRegisteredItemProducts,
  (registeredItemProducts: RegisteredItemProductDetails[]): boolean =>
    registeredItemProducts.length >= 1
);

export const selectRegisteredItemAppliances = createSelector(
  selectRegisteredItem,
  (
    registeredItem?: RegisteredItem
  ): RegistrationApplianceDetails[] | undefined => registeredItem?.appliances
);

export const selectRegisteredItemMerchandiseBrand = createSelector(
  selectRegisteredItem,
  (registeredItem?: RegisteredItem): string | undefined =>
    registeredItem?.merchandiseBrand
);

export const selectRegisteredItemQuoteNumber = createSelector(
  selectRegisteredItem,
  (registeredItem?: RegisteredItem): string | undefined =>
    registeredItem?.quoteNumber
);

export const selectHasRegisteredItemQuoteNumber = createSelector(
  selectRegisteredItem,
  (registeredItem?: RegisteredItem): boolean =>
    Boolean(registeredItem) && registeredItem
      ? registeredItem.quoteNumber !== 'no quote created for this appliance'
      : false
);

export const selectRegistrationCustomerDetails = createSelector(
  selectRegistrationState,
  (state: RegistrationState): CustomerDetails | undefined =>
    state.registrationCustomerDetails
);

export const selectRegistriaUserResponse = createSelector(
  selectRegistrationState,
  (state: RegistrationState): RegistriaUsersResponse | undefined =>
    state.registriaUserResponse
);

export const selectUserToken = createSelector(
  selectRegistrationState,
  (state: RegistrationState): string | undefined => state.userToken
);

export const selectHasRegisteredItemStandardPremium = createSelector(
  selectRegisteredItem,
  (registeredItem?: RegisteredItem): boolean =>
    Boolean(registeredItem?.standardPremium)
);

export const selectRegisteredItemHasAnnualPayment = createSelector(
  selectRegisteredItemProducts,
  (registeredItemProducts: RegisteredItemProductDetails[]): boolean =>
    registeredItemProducts.length > 1 &&
    registeredItemProducts[1].paymentFrequency === PaymentFrequency.Prepay
);

export const selectRegistriaUserRequest = createSelector(
  selectRegistrationState,
  (
    state: RegistrationState
  ): {
    userToken: string | undefined;
    registrationCustomerDetails: CustomerDetails | undefined;
    registrationRequest: RegistrationRequest | undefined;
    hasRegisteredItemQuoteNumber: boolean;
    registrationSystemCode: RegistrationSystemCode;
  } => ({
    userToken: state.userToken,
    registrationCustomerDetails: state.registrationCustomerDetails,
    registrationRequest: state.registrationRequest,
    hasRegisteredItemQuoteNumber:
      Boolean(state.registeredItem) && state.registeredItem
        ? state.registeredItem.quoteNumber !==
          'no quote created for this appliance'
        : false,
    registrationSystemCode: state.registrationSystemCode,
  })
);

// WIB Necessary State Selectors

export const selectQuote = createSelector(
  selectRegistrationState,
  (state: RegistrationState): QuoteResponse | undefined => state.quoteResponse
);

export const selectHasQuoteResponse = createSelector(
  selectQuote,
  (quote: QuoteResponse | undefined): boolean => Boolean(quote)
);

export const selectIsQuoteResponseSuccess = createSelector(
  selectQuote,
  (quote: QuoteResponse | undefined): boolean => Boolean(quote?.status === 200)
);

export const selectIsQuoteResponseNoContent = createSelector(
  selectRegistrationState,
  (state: RegistrationState): boolean =>
    Boolean(state.quoteError === 204 || state.quoteError === 503)
);

export const selectQuoteNetTotalAmount = createSelector(
  selectQuote,
  (state: QuoteResponse | undefined): number | undefined =>
    state?.offer.contracts[0].contractAttributes?.subscription?.cc
      ?.netTotalAmount
);

export const selectCheckoutInitialisation = createSelector(
  selectRegistrationState,
  (state: RegistrationState): CheckoutInitialisation | undefined =>
    state.checkoutInitialisation
);

export const selectPaymentFrequency = createSelector(
  selectRegistrationState,
  (state: RegistrationState): PaymentFrequency | undefined =>
    state.paymentFrequency
);

export const selectHasInitialisedCheckout = createSelector(
  selectCheckoutInitialisation,
  (checkoutInitialisation: CheckoutInitialisation | undefined): boolean =>
    Boolean(checkoutInitialisation)
);

export const selectCheckoutSuccessContractId = createSelector(
  selectRegistrationState,
  (state: RegistrationState): string | undefined =>
    state.checkoutSuccess?.contracts[0]?.contractId
);

export const selectCheckoutSuccessfulContract = createSelector(
  selectRegistrationState,
  (state: RegistrationState): CheckoutSuccessfulContract | undefined =>
    state.checkoutSuccess?.contracts[0]
);

export const selectQuoteEffectiveDate = createSelector(
  selectQuote,
  (state: QuoteResponse | undefined): string | undefined =>
    state?.offer.contracts[0].contractAttributes.effectiveDate
);

export const selectRegistriaUserRequestWib = createSelector(
  selectRegistrationState,
  (
    state: RegistrationState
  ): {
    userToken: string | undefined;
    registrationCustomerDetails: CustomerDetails | undefined;
    registrationRequest: RegistrationRequest | undefined;
    registrationSystemCode: RegistrationSystemCode;
  } => ({
    userToken: state.userToken,
    registrationCustomerDetails: state.registrationCustomerDetails,
    registrationRequest: state.registrationRequest,
    registrationSystemCode: state.registrationSystemCode,
  })
);

export const selectQuoteHasAnnualPayment = createSelector(
  selectQuote,
  (quote: QuoteResponse | undefined): boolean =>
    Boolean(quote?.offer.contracts[0].contractAttributes.annualPrepay)
);

export const selectCustomerFirstName = createSelector(
  selectCheckoutInitialisation,
  (
    checkoutInitialisation: CheckoutInitialisation | undefined
  ): string | undefined => checkoutInitialisation?.customer?.firstName
);

export const selectProtectionDetailsPaymentOptions = createSelector(
  selectQuote,
  (
    quote: QuoteResponse | undefined
  ): ProtectionDetailsPaymentOption[] | undefined => {
    return quote?.offer.contracts.flatMap(
      (contract): ProtectionDetailsPaymentOption[] => {
        return [
          {
            ...contract.contractAttributes.subscription,
            paymentFrequency: PaymentFrequency.Monthly,
          },
          {
            ...contract.contractAttributes.annualPrepay,
            paymentFrequency: PaymentFrequency.Annual,
          },
        ] as ProtectionDetailsPaymentOption[];
      }
    );
  }
);

export const selectPrepayPrice = createSelector(
  selectQuote,
  (state: QuoteResponse | undefined): number | undefined =>
    state?.offer.contracts[0].contractAttributes?.annualPrepay?.cc
      ?.netTotalAmount
);

export const selectSubscriptionPrice = createSelector(
  selectQuote,
  (state: QuoteResponse | undefined): number | undefined =>
    state?.offer.contracts[0].contractAttributes?.subscription?.cc
      ?.netTotalAmount
);

export const selectHasDiscountPrice = createSelector(
  selectQuote,
  (quote: QuoteResponse | undefined): boolean => {
    // Checkout quote cc payment for SUBSCRIPTION not annual prepay
    if (
      !quote ||
      !quote.offer.contracts.length ||
      !quote.offer.contracts[0].contractAttributes.subscription ||
      !quote.offer.contracts[0].contractAttributes.subscription.cc
        .discountProfileAttributes
    ) {
      return false;
    }
    const attributes = quote.offer.contracts[0].contractAttributes.subscription
      .cc.discountProfileAttributes as CheckoutDiscountProfileAttributesCC;

    return Boolean(attributes.ccDiscountCodeApplied);
  }
);

export const selectSubscriptionDiscountPrice = createSelector(
  selectQuote,
  (quote: QuoteResponse | undefined): number | undefined => {
    if (
      !quote ||
      !quote.offer.contracts[0].contractAttributes.subscription ||
      !quote.offer.contracts[0].contractAttributes.subscription.cc
        .discountProfileAttributes
    ) {
      return;
    }

    const attributes = quote.offer.contracts[0].contractAttributes.subscription
      .cc.discountProfileAttributes as CheckoutDiscountProfileAttributesCC;

    return attributes.netAmountBeforeDiscount;
  }
);
